'use client';

import {
  assertAllConsent,
  setupFidesListeners,
} from '@peloton/next/components/consentHelpers';
import type { AnalyticsJS } from './segment-analytics';
import type { EventType, Identity, Group, Load, Page } from './types';

declare global {
  interface Window {
    analytics: AnalyticsJS | undefined;
    gtag: (event: string, action: string, options: object) => void;
    fidesBucket: string;
    Optanon?: any;
    amplitude: any;
    ttq?: any;
    fbq?: any;
    uetq?: any;
    clarity?: any;
    toAppend?: any[];
    DD_RUM?: any;
    optimizely?: any;
    Fides?: {
      showModal: () => void;
      initialized: boolean;
      shouldShowExperience: () => boolean;
      identity: {
        email: string;
        fides_user_device_id: string;
      };
      consent: {
        functional: boolean;
        social_media: boolean;
        marketing: boolean;
        analytics: boolean;
        strictly_necessary: boolean;
      };
      config: {
        propertyId: string;
      };
      geolocation: {
        location: string;
        region: string;
        country: string;
      };
      experience: {
        experience_config: {
          name: string;
          id: string;
        };
        privacy_notices: [
          {
            translations: [
              {
                language: string;
                title: string;
                description: string;
                privacy_notice_history_id: string;
              },
            ];
          },
        ];
      };
    };
  }
}

// https://segment.com/docs/guides/filtering-data/#filtering-with-the-integrations-object
export const getSegmentIntegrations = () => {
  if (typeof window === 'undefined' || !window.Fides) {
    return {};
  }
  const config = { integrations: {} };
  const hasAnalytics = window.Fides?.consent.analytics ?? false;
  const hasMarketing = window.Fides?.consent.marketing ?? false;
  if (!hasAnalytics) {
    const analyticsIntegrations = {
      Amplitude: false,
      Optimizely: false,
    };
    config.integrations = { ...config.integrations, ...analyticsIntegrations };
  }
  if (!hasMarketing) {
    const marketingIntegrations = {
      Facebook: false,
      'Bing Ads': false,
      'Facebook Pixel': false,
      'TikTok Pixel': false,
      'Google Tag Manager': false,
    };
    config.integrations = { ...config.integrations, ...marketingIntegrations };
  }
  return config;
};

//https://segment.com/docs/privacy/consent-management/consent-in-segment-connections/#consent-object
// manual consent management in Segment requires adding a context object to the track call
export const maybeAddSegmentContext = (context?: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...context,
      context: {
        consent: {
          categoryPreferences: {
            functional: window?.Fides?.consent?.functional ?? false,
            social_media: window?.Fides?.consent?.social_media ?? false,
            marketing: window?.Fides?.consent?.marketing ?? false,
            analytics: window?.Fides?.consent?.analytics ?? false,
            strictly_necessary: window?.Fides?.consent?.strictly_necessary ?? false,
          },
        },
      },
    };
  } else {
    return {};
  }
};

export const getConsentIDs = (properties: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...properties,
      consentOrigin: 'fides',
      fides_id: window.Fides?.identity?.fides_user_device_id,
      uuid: crypto?.randomUUID?.(),
    };
  } else {
    return properties;
  }
};

export const maybeAddConsentOrigin = (properties: any) => {
  if (typeof window !== 'undefined' && window.Fides) {
    return {
      ...properties,
      consentOrigin: 'fides',
      fides_id: window.Fides?.identity?.fides_user_device_id,
      uuid: crypto?.randomUUID?.(),
    };
  } else {
    return properties;
  }
};

export interface AnalyticsInterface {
  track<T extends object>(event: EventType<T>): void;
  identify<T extends object>(payload: Identity<T>): void;
  group<T extends object>(group: Group<T>): void;
  reset(): void;
  load(load: Load): void;
  page<T extends object>(page: Page<T>): void;
  ready(callback: () => void): void;
  anonymousId(callback: (anonymousId: string) => void): void;
}

/**
 * Lazy-init `analytics` and call through, or throw if window.analytics object is not found
 *
 * @param {string} fn The SegmentAnalytics function to call
 * @param {any[]} args Arguments to pass along
 * @returns Result of calling through
 * @throws If Segment is not yet loaded (checks `window.analytics`)
 */
const initAndCallOrThrow = (fn: string, args: any) => {
  const winAnalytics =
    typeof window === 'undefined' ? false : (window?.analytics as unknown as AnalyticsJS);
  if (winAnalytics) {
    setAnalytics(winAnalytics);
    // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AnalyticsJS'.
    return winAnalytics[fn].apply(window, args);
  } else {
    throw Error(
      `Attempted to call ${fn}() in analytics before it is initialized, and no window.analytics exists.`,
    );
  }
};

/**
 * App wide analytics instance.
 */
let instance: AnalyticsInterface | any = {
  track: (...args: any[]) => initAndCallOrThrow('track', args),
  identify: (...args: any[]) => initAndCallOrThrow('identify', args),
  group: (...args: any[]) => initAndCallOrThrow('group', args),
  reset: (...args: any[]) => initAndCallOrThrow('reset', args),
  load: (...args: any[]) => initAndCallOrThrow('load', args),
  page: (...args: any[]) => initAndCallOrThrow('page', args),
  ready: (...args: any[]) => initAndCallOrThrow('ready', args),
  anonymousId: (...args: any[]) => initAndCallOrThrow('anonymousId', args),
};

export const setAnalytics = (analytics: AnalyticsInterface | AnalyticsJS) =>
  (instance = analytics);

export const identify = <T extends object>(payload: Identity<T>) => {
  const properties = maybeAddConsentOrigin(payload.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const event = { ...payload, properties, ...options };
  instance.identify(event, event, options);
};

export const track = <T extends object>(event: EventType<T>) => {
  const properties = maybeAddConsentOrigin(event.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const payload = { ...event, properties, options };
  return instance.track(payload, payload, options);
};

export const group = <T extends object>(event: Group<T>) => {
  instance.group(event);
};

export const reset = () => {
  instance.reset();
};

export const load = (config: Load, analytics?: AnalyticsInterface) => {
  if (analytics) {
    instance = analytics;
  }
  // Fides CRA apps
  setupFidesListeners();
  const waitForSegment = setInterval(() => {
    if (typeof window !== 'undefined' && window.analytics && !window.Fides) {
      instance.load(config);
      clearInterval(waitForSegment);
      return;
    }
  }, 500);
  const waitForFides = setInterval(() => {
    if (
      typeof window !== 'undefined' &&
      window.analytics &&
      window.Fides?.initialized &&
      (window.Fides?.consent.marketing || window.Fides?.consent.analytics)
    ) {
      instance.load(config);
      instance.ready(() => {
        assertAllConsent();
      });
      clearInterval(waitForFides);
    }
  }, 500);
};

export const page = <T extends object>(event: Page<T>) => {
  const properties = maybeAddConsentOrigin(event.properties);
  const context = maybeAddSegmentContext();
  const integrations = getSegmentIntegrations();
  const options = {
    ...integrations,
    ...context,
  };
  const payload = { ...event, properties, options };
  instance.page(payload, undefined, options);
};

export const anonymousId = (callback: (anonymousId: string) => void) => {
  instance.anonymousId(callback);
};

export const ready: AnalyticsInterface['ready'] = cb => {
  instance?.ready(cb);
};
